import React from 'react';
import { FileText, AlertTriangle } from 'lucide-react';
import SEO from '../components/SEO';

const Terms = () => {
  return (
    <div className="min-h-screen pt-20 bg-black">
      <SEO title="Terms & Conditions | PopCrown Official" description="PopCrown terms and conditions. The PopCrown app is free; PopCrown Community membership (£1.99/month) terms, usage policy, and community guidelines." keywords="PopCrown terms, PopCrown conditions, PopCrown Community terms, free app terms" canonical="/terms" />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="blog-card p-8 md:p-12">
          <div className="flex items-center gap-4 mb-8">
            <FileText className="w-12 h-12 text-[#E10600]" />
            <h1 className="text-4xl font-bold text-white">Terms & Conditions</h1>
          </div>

          <p className="text-gray-400 mb-8">
            <strong>Effective Date:</strong> June 2026<br />
            <strong>Last Updated:</strong> June 2026
          </p>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">1. Agreement to Terms</h2>
              <p className="mb-4">
                By accessing or using the PopCrown Official Ltd website ("PopCrown", "we", "us", or "our") and the PopCrown Social mobile application, you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you may not access our services.
              </p>
              <p>
                These Terms apply to all visitors, users, and others who access or use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">2. Use License</h2>
              <p className="mb-4">
                PopCrown Official Ltd grants you a limited, non-exclusive, non-transferable, revocable license to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Access and use the website and mobile application</li>
                <li>Download and use the app on your personal devices</li>
                <li>Access content for personal, non-commercial purposes</li>
              </ul>
              <p className="mb-4"><strong className="text-white">You may not:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Modify, copy, or reproduce the service for commercial purposes</li>
                <li>Attempt to reverse engineer or decompile any part of the service</li>
                <li>Remove any copyright or proprietary notations</li>
                <li>Transfer the materials to another person or "mirror" on another server</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">3. User Accounts</h2>
              <h3 className="text-xl font-semibold text-white mb-3">3.1 Account Creation</h3>
              <p className="mb-4">
                To access certain features of PopCrown Social, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your information</li>
                <li>Maintain the security of your password</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">3.2 Age Requirement</h3>
              <p className="mb-3">
                The free PopCrown app is available to users of all ages. There is no minimum age requirement to download or use the free app features (daily quizzes, the Legal Vault, In His Words, the global leaderboard).
              </p>
              <p className="mb-3">
                <strong>You must be at least 16 years old to access the community features of this app</strong>, including but not limited to the PopCrown Community feed, posting content, commenting, direct messaging, and any social interaction with other members. This age restriction applies to PopCrown Community membership (£1.99/month).
              </p>
              <p>
                By creating a PopCrown Community account or otherwise accessing community features, you represent and warrant that you are at least 16 years old. This restriction is in place to comply with the UK Online Safety Act, UK GDPR, and our community safety standards. We reserve the right to suspend or terminate any account where we have reason to believe the user does not meet this age requirement.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">4. The PopCrown App and PopCrown Community Membership</h2>
              <h3 className="text-xl font-semibold text-white mb-3">4.1 Free App</h3>
              <p className="mb-4">
                The core PopCrown mobile and web app is provided <strong>free of charge</strong>, with no card or subscription required. Free app features include: daily MJ quizzes &amp; games, the Legal Vault (documents and timelines), In His Words (Michael&apos;s quotes), the global leaderboard, and 130+ language support. We reserve the right to change, withdraw, or limit free features at our discretion, but we will give reasonable notice where practical.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">4.2 PopCrown Community Membership</h3>
              <p className="mb-4">
                The PopCrown Community is an optional paid membership that unlocks additional features on top of the free app:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>PopCrown Community:</strong> £1.99 per month — Includes everything in the free app, plus the community feed, early access to event tickets, exclusive discount codes (merch, tickets, partner perks), Fan Points and members-only badges.</li>
              </ul>
              <p className="mb-4">
                Your PopCrown Community fee directly supports hosting, security, moderation, development and legal compliance — keeping the community safe, ad-free and properly maintained.
              </p>
              <p className="mb-4">
                Access is available immediately upon payment via desktop, tablet, and mobile.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">4.3 Billing</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>PopCrown Community subscriptions automatically renew monthly unless cancelled</li>
                <li>You will be charged at the beginning of each monthly billing cycle</li>
                <li>Prices are subject to change with 30 days&apos; notice</li>
                <li>All payments are processed securely by Stripe</li>
                <li>Promotional codes may be available and are subject to their own terms</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">4.4 Cancellation</h3>
              <p className="mb-2">
                You may cancel your PopCrown Community subscription at any time through your account settings or the Stripe customer portal. Upon cancellation:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>You will retain PopCrown Community access until the end of your current billing period</li>
                <li>Your subscription will not renew at the end of the billing period</li>
                <li>The free PopCrown app remains yours to use at no charge</li>
                <li>No partial refunds will be provided for unused time remaining in your billing period</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-white mb-3">4.5 Refund Policy & Right to Cancel</h3>
              
              <div className="bg-[#1a1a1a] border border-[#E10600]/30 rounded-lg p-4 mb-4">
                <p className="text-white font-semibold mb-2 inline-flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#E10600]" strokeWidth={2} aria-hidden="true" /> Important Notice Regarding Your Right to Cancel</p>
                <p className="text-gray-300">
                  PopCrown Social is an <strong className="text-white">instantly accessible digital content service</strong>. By subscribing and accessing the service, you acknowledge and agree that:
                </p>
              </div>
              
              <p className="mb-4">
                Under the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013 and the Consumer Rights Act 2015, you have a statutory 14-day cooling-off period for online purchases. However, for digital content services like PopCrown Social:
              </p>
              
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong className="text-white">Waiver of Cooling-Off Period:</strong> By subscribing and immediately accessing PopCrown Social, you expressly consent to the immediate provision of the digital service and acknowledge that you <strong className="text-white">lose your 14-day right to cancel</strong> once you begin using the service.</li>
                <li><strong className="text-white">Instant Access:</strong> PopCrown Social provides instant access to digital content upon subscription, similar to streaming services like Netflix. Once you access premium features, the service has been fully provided.</li>
                <li><strong className="text-white">No Refunds for Used Services:</strong> As the digital content is made available immediately and you have consented to this, refunds are not available for subscription periods where you have accessed the service.</li>
              </ul>

              <p className="mb-4 text-white font-semibold">Refunds Will Only Be Provided In The Following Circumstances:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Technical Failure:</strong> If you are completely unable to access the service due to technical problems on our end for more than 7 consecutive days, and we are unable to resolve the issue</li>
                <li><strong>Duplicate Charges:</strong> If you were charged multiple times in error for the same subscription period</li>
                <li><strong>Unauthorized Transactions:</strong> If your payment method was used without your authorization (subject to verification)</li>
              </ul>

              <p className="mb-4">
                <strong className="text-white">Non-Refundable Items:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Promotional offers, discount codes, and special pricing are non-refundable</li>
                <li>Partial months or years of subscription are non-refundable upon cancellation</li>
              </ul>
              
              <p className="mb-4">
                This policy is consistent with how digital subscription services handle refunds for instantly accessible digital content under UK and EU consumer law.
              </p>

              <p className="mt-4">
                To request a refund for an eligible circumstance, contact <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a> with your account email, transaction details, and reason for the request. We aim to respond within 5 business days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">5. User Content and Conduct</h2>
              <h3 className="text-xl font-semibold text-white mb-3">5.1 Content Ownership</h3>
              <p className="mb-4">
                You retain ownership of content you post (posts and comments). By posting, you grant PopCrown a worldwide, non-exclusive, royalty-free license to use, display, and distribute your content within the service.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">5.2 Prohibited Conduct</h3>
              <p className="mb-2">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Post content that is illegal, harmful, threatening, abusive, or defamatory</li>
                <li>Harass, bully, or harm other users</li>
                <li>Impersonate any person or entity</li>
                <li>Post spam, advertisements, or promotional content</li>
                <li>Violate intellectual property rights</li>
                <li>Distribute viruses or malicious code</li>
                <li>Interfere with the proper functioning of the service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">6. Intellectual Property</h2>
              <h3 className="text-xl font-semibold text-white mb-3">6.1 PopCrown Content</h3>
              <p className="mb-4">
                All content on PopCrown (text, graphics, logos, code) is owned by PopCrown Official Ltd and protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">6.2 Third Party Content</h3>
              <p className="mb-4">
                PopCrown Official Ltd is not affiliated with any artist estates, rights holders, or official entities. All trademarks, names, and imagery are property of their respective owners. We use such content under fair use principles, Creative Commons licenses, or with permission. We do not host copyrighted music, videos, or photographs - all media links direct to authorized platforms.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">6.3 DMCA Compliance</h3>
              <p>
                If you believe any content infringes your copyright, please contact us at <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a> with details of the alleged infringement.
              </p>
            </section>

            <section id="editorial-standards">
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">7. Editorial Standards</h2>
              <p className="mb-4">
                PopCrown Official Ltd is committed to providing accurate, factual, and responsibly sourced content, particularly regarding legal matters, historical events, and biographical information.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">7.1 Sourcing Standards</h3>
              <p className="mb-4">
                All factual claims on PopCrown, especially those in our Legal Vault and educational content, are sourced from:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong className="text-white">Official Government Records:</strong> FBI Vault (vault.fbi.gov), court records, and other publicly available government documents released under the Freedom of Information Act (FOIA).</li>
                <li><strong className="text-white">Court Documents:</strong> Official trial transcripts, verdict documents, and legal filings from Santa Barbara Superior Court, Los Angeles Superior Court, and other jurisdictions.</li>
                <li><strong className="text-white">Primary Sources:</strong> Direct quotes, official statements, and documented evidence from credible, verifiable sources.</li>
                <li><strong className="text-white">Reputable News Archives:</strong> Historical reporting from established news organisations, used for context and timeline verification.</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">7.2 Fact Verification Process</h3>
              <p className="mb-4">
                Before publishing any factual content, particularly legal or biographical information, we:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Cross-reference claims with multiple independent sources</li>
                <li>Prioritise primary sources (court documents, official records) over secondary reporting</li>
                <li>Clearly distinguish between verified facts and analysis/opinion</li>
                <li>Include source citations for all factual claims</li>
                <li>Date-stamp content to indicate when information was last verified</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">7.3 Legal Vault Standards</h3>
              <p className="mb-4">
                Our Legal Vault section adheres to the highest standards of accuracy:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong className="text-white">Document Authenticity:</strong> All documents are sourced directly from official government websites (FBI Vault, court record systems) or certified copies.</li>
                <li><strong className="text-white">No Editorialisation:</strong> Legal documents are presented as-is without modification. Any summaries or explanations are clearly marked as PopCrown editorial content.</li>
                <li><strong className="text-white">Context Provision:</strong> Where necessary, we provide historical context to help readers understand documents, clearly labelled as editorial commentary.</li>
                <li><strong className="text-white">Verdict Accuracy:</strong> All court verdicts and legal outcomes are reported exactly as documented in official court records.</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">7.4 Corrections Policy</h3>
              <p className="mb-4">
                If we discover an error in our content, or if an error is reported to us:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>We will investigate promptly and correct any verified inaccuracies</li>
                <li>Significant corrections will be noted with a correction notice and date</li>
                <li>We welcome feedback and corrections from our community at <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a></li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">7.5 Independence Statement</h3>
              <p className="mb-4">
                PopCrown Official Ltd operates independently and is not affiliated with, endorsed by, or connected to any artist estates, rights holders, record labels, or management companies. Our editorial decisions are made independently based on the principles outlined above.
              </p>
              <p>
                We are a fan community dedicated to preserving accurate historical information and providing a safe space for pop culture fans worldwide.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">8. Disclaimers</h2>
              <h3 className="text-xl font-semibold text-white mb-3">8.1 Service "As Is"</h3>
              <p className="mb-4">
                Our services are provided "as is" and "as available" without warranties of any kind, express or implied. We do not guarantee the service will be uninterrupted, secure, or error-free.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">8.2 Third-Party Content</h3>
              <p>
                We link to third-party websites and services (Spotify, YouTube, etc.). We are not responsible for the content, accuracy, or practices of these third parties.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">9. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, PopCrown Official Ltd shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">10. Termination</h2>
              <p className="mb-4">
                We reserve the right to terminate or suspend your account immediately, without prior notice, for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violation of these Terms</li>
                <li>Fraudulent or illegal activity</li>
                <li>Requests by law enforcement</li>
                <li>Extended inactivity</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">11. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to conflict of law provisions. Any disputes shall be resolved in the courts of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">12. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or app notification. Your continued use after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">13. Copyright and Takedown Policy</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">13.1 Who We Are</h3>
              <p className="mb-4">
                This website is operated by PopCrown Official Ltd ("PopCrown", "we", "us"). We respect intellectual property rights and expect our community members and contributors to do the same.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">13.2 Our Position on Third Party Rights</h3>
              <p className="mb-4">
                PopCrown is an independent fan community. We are not affiliated with, endorsed by, authorised by, or connected to any artists, brands, record labels, publishers, management companies, or any artist estates or rights holders. All trademarks, artist names, logos, and related imagery are the property of their respective owners. Any references are used for identification and commentary purposes only. Unless stated otherwise, PopCrown does not claim ownership of any third party rights.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">13.3 Content We Host and Content We Link To</h3>
              <p className="mb-4">
                We do not host or distribute copyrighted music, videos, or photographs. Where third party media is referenced, we may link to content hosted on publicly available platforms and, where possible, official or licensed sources. PopCrown is not responsible for the content hosted on third party websites.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">13.4 Reporting a Concern</h3>
              <p className="mb-4">
                If you believe that any content on this website infringes your copyright, trademark, or other intellectual property rights, please contact us at <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a> with the information below. To help us review your request quickly, please include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Your full name and contact details</li>
                <li>The URL(s) of the page(s) on our site where the material appears</li>
                <li>A clear description of the material you believe is infringing</li>
                <li>An explanation of the rights you own or control (for example, proof of ownership or authority to act for the rights holder)</li>
                <li>Where relevant, the original source or a link to an authorised version</li>
                <li>A statement that the information you provide is accurate and that you are the rights holder or authorised to act on their behalf</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">13.5 What Happens Next</h3>
              <p className="mb-4">
                We will review all valid requests promptly. Where appropriate, we may:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Remove the content</li>
                <li>Disable access to the content</li>
                <li>Request additional information to verify rights or clarify the issue</li>
                <li>Contact the uploader or contributor (if applicable)</li>
              </ul>
              <p className="mb-4">
                We may also take steps to prevent repeat issues where necessary.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">13.6 Counter Statements</h3>
              <p className="mb-4">
                If you believe content was removed or disabled in error, you can reply to our notification (or email <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a>) with:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>The URL(s) affected</li>
                <li>The reason you believe the removal was incorrect</li>
                <li>Any supporting information that helps us verify your position</li>
              </ul>
              <p className="mb-4">
                We will review counter statements and, where appropriate, reinstate content.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">13.7 Changes to This Policy</h3>
              <p>
                We may update this policy from time to time. Any changes will be posted on this page with an updated effective date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">14. Contact Information</h2>
              <p className="mb-4">
                For questions about these Terms, please contact:
              </p>
              <div className="bg-[#1a1a1a] p-4 rounded-lg">
                <p><strong className="text-white">PopCrown Official Ltd</strong></p>
                <p>Email: <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a></p>
              </div>
            </section>

            <section className="border-t border-gray-700 pt-6 mt-8">
              <p className="text-sm text-gray-400">
                By using PopCrown Official and PopCrown Social, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;